import { useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect, createContext, useContext } from "react";
import FiltroUtils from "../utils/FiltroUtils";

/**
 * Hook personalizado para gerenciar filtros e paginação na URL
 *
 * @param {Object} defaultFilters - Filtros padrão (valores iniciais)
 * @param {Number} defaultPage - Página inicial (padrão: 0)
 * @param {Number} defaultSize - Tamanho da página (padrão: 10)
 * @param {String} basePath - Caminho base da URL (ex: "/usuario_")
 * @returns {Object} Objeto com estados e funções para gerenciar filtros e paginação
 */

export function useUrlFilters(defaultFilters, defaultPage = 0, defaultSize = 10, basePath = "") {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const getParamValue = (param, defaultValue = "") => {
    if (!searchParams) return defaultValue;
    const value = searchParams.get(param);
    return value !== null ? value : defaultValue;
  };

  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [pageSize, setPageSize] = useState(defaultSize);
  const [filtros, setFiltros] = useState(defaultFilters);

  useEffect(() => {
    if (searchParams) {
      // Atualiza a página
      const pageFromUrl = parseInt(getParamValue('page', defaultPage.toString()));
      setCurrentPage(pageFromUrl);

      // Atualiza o tamanho da página
      const sizeFromUrl = parseInt(getParamValue('size', defaultSize.toString()));
      setPageSize(sizeFromUrl);

      // Atualiza os filtros
      const filtersFromUrl = {};
      Object.keys(defaultFilters).forEach(key => {
        filtersFromUrl[key] = getParamValue(key, defaultFilters[key]);
      });
      setFiltros(filtersFromUrl);
    }
  }, [searchParams]);

  const atualizarURL = (page, size, filtrosObj) => {
    const queryObj = { page, size, ...filtrosObj };

    Object.keys(queryObj).forEach(key => {
      if (queryObj[key] === "" || queryObj[key] === null || queryObj[key] === undefined) {
        delete queryObj[key];
      }
    });

     const queryString = FiltroUtils.buildQuery(queryObj);
    navigate(`${basePath}${queryString}`);
  };

  const aplicarFiltros = () => {
    atualizarURL(currentPage, pageSize, filtros);

    return {
      page: currentPage,
      size: pageSize,
      filtros,
      queryString: FiltroUtils.applyPageFilter(currentPage, pageSize, filtros),
    };
  };

  const limparFiltros = () => {
    setFiltros(defaultFilters);
    setCurrentPage(0);
    atualizarURL(0, pageSize, defaultFilters);
  };

  const proximaPagina = () => {
    const newPage = currentPage + 1;
    setCurrentPage(newPage);
    atualizarURL(newPage, pageSize, filtros);
    return newPage;
  };

  const paginaAnterior = () => {
    const newPage = Math.max(0, currentPage - 1);
    setCurrentPage(newPage);
    atualizarURL(newPage, pageSize, filtros);
    return newPage;
  };

  const irParaPagina = (page) => {
    const newPage = Math.max(0, page);
    setCurrentPage(newPage);
    atualizarURL(newPage, pageSize, filtros);
    return newPage;
  };

  const alterarTamanhoPagina = (size) => {
    setPageSize(size);
    setCurrentPage(0);
    atualizarURL(0, size, filtros);
  };

  return {
    currentPage,
    pageSize,
    filtros,
    setFiltros,
    aplicarFiltros,
    limparFiltros,
    proximaPagina,
    paginaAnterior,
    irParaPagina,
    alterarTamanhoPagina
  };
}

// Criando um contexto para os filtros
const UrlFiltersContext = createContext(null);

// Hook para acessar o contexto facilmente
export const useUrlFiltersContext = () => {
  const context = useContext(UrlFiltersContext);
  if (!context) {
    throw new Error("useUrlFiltersContext deve ser usado dentro de um FiltroURLWrapper");
  }
  return context;
};

/**
 * Componente wrapper para prover funcionalidade de filtros de URL
 * @param {Object} props - Propriedades do componente
 * @param {Object} props.defaultFilters - Filtros padrão
 * @param {Number} props.defaultPage - Página inicial (padrão: 0)
 * @param {Number} props.defaultSize - Tamanho da página (padrão: 10)
 * @param {String} props.basePath - Caminho base da URL
 * @param {React.ReactNode|Function} props.children - Componentes filhos ou função de renderização
 * @returns {React.Component} Componente React
 */
export function FiltroURLWrapper({ 
  defaultFilters, 
  defaultPage = 0, 
  defaultSize = 10, 
  basePath = "", 
  children 
}) {
  const filterHook = useUrlFilters(defaultFilters, defaultPage, defaultSize, basePath);
  
  return (
    <UrlFiltersContext.Provider value={filterHook}>
      {typeof children === 'function' 
        ? children(filterHook) 
        : children}
    </UrlFiltersContext.Provider>
  );
}

export default FiltroURLWrapper;
